<template>
  <div class="log">

    <header class="masthead">

      <h1 class="title">{{ displayName }}<span class="dot">.</span></h1>

      <p v-if="subtitle" class="subtitle">{{ subtitle }}</p>
    </header>

    <!-- Date range -->
    <div class="panel">
      <label class="field">
        <span class="field-label">From</span>
        <input v-model="from" type="date" class="input" @change="run">
      </label>

      <label class="field">
        <span class="field-label">To</span>
        <input v-model="to" type="date" class="input" @change="run">
      </label>

      <div class="quick">
        <button class="chip" @click="setRange(0)">Today</button>
        <button class="chip" @click="setRange(6)">7 days</button>
        <button class="chip" @click="setRange(29)">30 days</button>
        <button class="chip" @click="setRange(89)">90 days</button>
      </div>

      <button
        class="btn"
        :disabled="days.length === 0"
        @click="exportCsv"
      >Export CSV</button>
    </div>

    <div v-if="loading" class="loading">
      <span class="pulse"></span>
      <span>Reading the log</span>
    </div>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="run">Try again</button>
    </p>

    <!-- Days, not badge events.
         A day worked from home has a status and no badge events at all, so
         testing events here hid the very rows that were added to explain the
         gap: the list was built correctly and then never rendered. -->
    <p v-else-if="days.length === 0" class="msg">Nothing recorded in this range</p>

    <!-- Grouped by day, most recent first -->
    <div v-else class="days">
      <section v-for="day in days" :key="day.date" class="day">
        <header class="day-head">
          <h2 class="day-date">{{ formatDate(day.date) }}</h2>
          <div class="day-stats">
            <span class="day-stat">
              <b>{{ day.total }}</b> total
            </span>
            <span class="day-stat">
              <b>{{ day.visits }}</b>
              {{ day.visits === 1 ? 'visit' : 'visits' }}
            </span>
            <!-- Next to the hours, because it explains them: a day at home has
                 no badge events and so no total, and without this the row reads
                 as a day somebody did not turn up. -->
            <span v-if="day.status" class="day-status">
              <span class="day-status-icon">{{ day.status.icon }}</span>
              {{ day.status.label }}
              <template v-if="day.status.startTime && day.status.endTime">
                {{ day.status.startTime }}&ndash;{{ day.status.endTime }}
              </template>

              <!-- Started and not yet finished. Shown rather than left blank,
                   because the day's total is short by however long they carried
                   on working, and a bare total would read as the whole day. -->
              <template v-else-if="day.status.startTime">
                from {{ day.status.startTime }}, not finished
              </template>
            </span>

            <span v-if="day.unmatched > 0" class="day-flag">
              {{ day.unmatched }} unmatched
            </span>
          </div>
        </header>

        <ol class="events">
          <li
            v-for="(e, i) in day.events"
            :key="i"
            class="event"
            :class="e.direction === 'ENTERED' ? 'ev-in' : 'ev-out'"
          >
            <span class="ev-time">{{ e.time }}</span>
            <span class="ev-dir">{{ e.direction === 'ENTERED' ? 'In' : 'Out' }}</span>
            <span class="ev-loc">
              {{ e.location || '\u2014' }}
              <!-- Typed in by an admin, not a tap. Said plainly, with who and
                   why, so a corrected day is never mistaken for a recorded one. -->
              <span
                v-if="e.source === 'manual'"
                class="ev-manual"
                :title="'Entered by ' + (e.entered_by || 'an admin') + (e.note ? ': ' + e.note : '')"
              >manual<template v-if="e.note"> &middot; {{ e.note }}</template></span>
            </span>
            <span class="ev-dur">{{ e.duration || '' }}</span>
          </li>
        </ol>
      </section>
    </div>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { getDetailReport, toCsv, downloadCsv } from '@/services/ReportService'
import { getStatusesRange, getStatusTypes, statusMeta } from '@/services/StatusService'
import { authState } from '@/auth/authService'
import { getPersonCompanyGroup } from '@/services/CompanyGroupService'

const route = useRoute()

// Reached two ways: from the reports table, which knows Vero's user_id, or from
// the site roster, which only knows the person's Entra email. Whichever is
// present is passed straight through as a filter.
// Reached three ways: from the reports table, which knows Vero's user_id; from
// the site roster, which knows the person's Entra email; or as /registrations
// with nothing, meaning the signed-in person's own record.
// For the signed-in person's own record the object id goes too. Manual
// entries are keyed on it, and somebody with no badge mapped has no email
// in the badge table for the backend to turn into one. The backend accepts
// either, or both.
const userId = computed(() =>
  route.query.user_id
  || (route.query.email ? '' : (authState.account?.localAccountId ?? ''))
)

const email = computed(() =>
  route.query.email || (route.query.user_id ? '' : (authState.account?.username ?? ''))
)

const nameFromQuery = computed(() =>
  route.query.name || (route.query.user_id || route.query.email ? '' : (authState.account?.name ?? ''))
)

const events = ref([])
const statuses = ref([])
const loading = ref(true)
const error = ref(null)

function isoDate(d) { return d.toISOString().slice(0, 10) }

const today = new Date()
const weekAgo = new Date()
weekAgo.setDate(weekAgo.getDate() - 6)

// A range passed in the query wins, so arriving from the reports table keeps
// the period the person was already looking at.
const from = ref(route.query.from || isoDate(weekAgo))
const to = ref(route.query.to || isoDate(today))

const displayName = computed(() =>
  nameFromQuery.value || events.value[0]?.user_name || 'Activity log'
)

// The person's company group, read once per person. Null until known, or
// when the lookup fails: then the line simply has no company on it.
const company = ref(null)

async function loadCompany() {
  company.value = null
  if (!userId.value && !email.value) return
  try {
    company.value = await getPersonCompanyGroup({ userId: userId.value, email: email.value })
  } catch (err) {
    console.error(err)
  }
}

const subtitle = computed(() => {
  const first = events.value[0]
  // The site of the latest event, then the company: "11 • Company: EPOKA".
  // With no events the email stands in for the site. The department was
  // always empty here, so it is not part of this.
  const parts = [
    first ? (first.location ?? '') : (email.value || ''),
    company.value?.name ? `Company: ${company.value.name}` : ''
  ]
  return parts.filter(Boolean).join(' • ')
})

function setRange(daysBack) {
  const start = new Date()
  start.setDate(start.getDate() - daysBack)
  from.value = isoDate(start)
  to.value = isoDate(new Date())
  run()
}

function formatDate(iso) {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  })
}

function minutesOf(time) {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

function formatDuration(minutes) {
  if (minutes <= 0) return ''
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`
}

/**
 * Group events by day and pair each entry with the exit that follows it.
 *
 * The pairing mirrors the backend's daily summary deliberately: an entry with
 * no exit, or an exit with no entry, is counted as unmatched and contributes no
 * time. Guessing at the missing half would invent hours nobody worked.
 */
/**
 * Every date from start to end inclusive, as YYYY-MM-DD.
 *
 * Built by stepping a UTC date so it cannot skip or repeat a day across a
 * daylight-saving boundary, which local-time arithmetic does twice a year.
 */
function datesBetween(start, end) {
  const out = []
  const at = new Date(start + 'T00:00:00Z')
  const last = new Date(end + 'T00:00:00Z')

  // A malformed range would otherwise loop forever.
  if (Number.isNaN(at.getTime()) || Number.isNaN(last.getTime())) return out

  let guard = 0
  while (at <= last && guard++ < 400) {
    out.push(at.toISOString().slice(0, 10))
    at.setUTCDate(at.getUTCDate() + 1)
  }

  return out
}

/**
 * The status covering a given date, if any.
 *
 * Statuses are stored as a range, so a week of leave is one row rather than
 * five, and every day inside it has to find its way back to that row.
 */
function statusOn(date) {
  const found = statuses.value.find((x) => x.start_date <= date && date <= x.end_date)
  if (!found) return null

  const meta = statusMeta(found.status)
  return {
    label: meta?.label ?? found.status,
    icon: meta?.icon ?? '',
    // Given by the person when they set the status. Empty for a status that
    // does not ask, and for anything recorded before the question existed.
    startTime: found.start_time ?? '',
    endTime: found.end_time ?? ''
  }
}

/** Minutes between two HH:MM times, or 0 if either is missing. */
function statusMinutes(status) {
  if (!status?.startTime || !status?.endTime) return 0

  const mins = minutesOf(status.endTime) - minutesOf(status.startTime)
  return mins > 0 ? mins : 0
}

const days = computed(() => {
  const byDate = new Map()

  for (const e of events.value) {
    if (!byDate.has(e.date)) byDate.set(e.date, [])
    byDate.get(e.date).push({ ...e })
  }

  // Days with a status but no badge events at all: exactly the case that used
  // to leave a hole in the log.
  for (const x of statuses.value) {
    for (const date of datesBetween(x.start_date, x.end_date)) {
      if (date < from.value || date > to.value) continue
      if (!byDate.has(date)) byDate.set(date, [])
    }
  }

  const out = []

  for (const [date, list] of byDate) {
    list.sort((a, b) => a.time.localeCompare(b.time))

    let openIndex = -1
    let totalMinutes = 0
    let visits = 0
    let unmatched = 0

    list.forEach((e, i) => {
      if (e.direction === 'ENTERED') {
        if (openIndex !== -1) unmatched++   // never closed
        openIndex = i
        visits++
      } else {
        if (openIndex === -1) {
          unmatched++
        } else {
          const mins = minutesOf(e.time) - minutesOf(list[openIndex].time)
          if (mins > 0) {
            totalMinutes += mins
            e.duration = formatDuration(mins)
          }
          openIndex = -1
        }
      }
    })

    if (openIndex !== -1) unmatched++

    out.push({
      date,
      // Shown beside the hours. A half day at home keeps its badge events and
      // gains the status; a full day has the status and nothing else.
      status: statusOn(date),
      // Newest first within the day reads better for "what happened today",
      // which is the common question.
      events: list.slice().reverse(),
      // Hours worked from home count toward the day, where they were given.
      //
      // They are added rather than replacing the badge total: a half day at
      // home and a half in the office is one working day, and showing only one
      // half would understate it either way.
      total: formatDuration(totalMinutes + statusMinutes(statusOn(date))) || '\u2014',
      visits,
      unmatched
    })
  }

  return out.sort((a, b) => b.date.localeCompare(a.date))
})

async function run() {
  loading.value = true
  error.value = null

  try {
    const filters = { from: from.value, to: to.value }
    if (userId.value) filters.user_id = userId.value
    if (email.value) filters.user_email = email.value

    // Statuses as well as badge events.
    //
    // A day spent working from home has no badge events at all, so the log
    // showed nothing for it and read as a day somebody did not turn up. The
    // status is the explanation, and it belongs next to the gap it explains.
    //
    // Failing is not fatal: the badge events still render, which is what the
    // page is chiefly for.
    const [detail, statusList] = await Promise.all([
      getDetailReport(filters),
      getStatusesRange(from.value, to.value).catch(() => []),

      // The status DEFINITIONS, so a status somebody added under Admin >
      // Statuses shows its name and icon rather than its raw code. Appended
      // last: it is not destructured, so no existing result shifts position.
      getStatusTypes().catch(() => [])
    ])

    // Already in the office's own local time: the backend converts each event
    // to the zone of the country it happened in. Not the viewer's clock, so a
    // Copenhagen admin reading a Singapore log sees Singapore times.
    events.value = detail

    // Matched on email, the only identifier both sides share: a status records
    // who set it, a badge event records the card that tapped. Somebody reached
    // by user_id alone has no email here and so shows no statuses, which is a
    // gap worth knowing about rather than a silent wrong answer.
    const mine = email.value.toLowerCase()
    statuses.value = mine
      ? (statusList ?? []).filter((x) => (x.email ?? '').toLowerCase() === mine)
      : []
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

function exportCsv() {
  const columns = [
  // Department is deliberately absent.
  //
  // It used to come from UserTranslation, which is empty now that badges are
  // mapped in BadgeMapping and departments come from Entra. The report resolves
  // people through the badge mapping, which carries a name and an email but no
  // department, so the column was always blank -- on screen and in the export.
  //
  // An always-empty column is worse than no column: it looks like missing data
  // rather than a thing that is not recorded here.
    { key: 'date', label: 'Date' },
    { key: 'time', label: 'Time' },
    { key: 'user_name', label: 'Name' },
    { key: 'location', label: 'Site' },
    { key: 'direction', label: 'Direction' },
    // Door or manual, and for manual who entered it and why.
    { key: 'source', label: 'Source' },
    { key: 'entered_by', label: 'Entered by' },
    { key: 'note', label: 'Reason' },

    // Declared days appear too, otherwise a week worked from home exported as
    // an empty file with headers. Blank on a badge row, which is honest: that
    // row is a door event and has no status attached.
    { key: 'status', label: 'Status' }
  ]

  // Badge events, plus one row per day that exists only because of a status.
  // Built from the same day list the page shows, so the file matches the
  // screen rather than being assembled separately and drifting from it.
  const rows = []

  for (const day of days.value) {
    const declared = day.status
      ? [day.status.label, day.status.startTime && day.status.endTime
          ? `${day.status.startTime}-${day.status.endTime}`
          : day.status.startTime ? `from ${day.status.startTime}` : '']
        .filter(Boolean).join(' ')
      : ''

    if (day.events.length === 0) {
      rows.push({
        date: day.date,
        time: '',
        user_name: displayName.value,
        location: '',
        direction: '',
        status: declared
      })
      continue
    }

    // Oldest first in the file, which is the order a log is read in, even
    // though the screen shows the newest at the top of each day.
    for (const e of day.events.slice().reverse()) {
      rows.push({ ...e, status: declared })
    }
  }

  const csv = toCsv(columns, rows)
  const who = (displayName.value || 'person').replace(/[^a-z0-9]+/gi, '-').toLowerCase()
  downloadCsv(`vero-log-${who}-${from.value}-to-${to.value}.csv`, csv)
}


onMounted(() => {
  run()
  loadCompany()
})
</script>


<style scoped>
.log {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;
  --wash: #f7f9fb;
  --paper: #ffffff;
  --in: #0f9d58;

  /* Left aligned, not centred. Centring made the content drift right of the
     menu and the logo, so nothing lined up with anything else. The max-width
     stays: it stops a line of text running the width of a wide monitor. */
  max-width: 1100px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  padding: 28px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 24px;
}


.title {
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 14px 0 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

.subtitle {
  font-size: 15px;
  color: var(--ink-soft);
  margin: 8px 0 0;
}

/* ---------- Controls ---------- */

.panel {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px;
  margin-bottom: 28px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.input {
  font-family: inherit;
  font-size: 15px;
  height: 44px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--paper);
  outline: none;
}

.input:focus { border-color: var(--ink); }

.quick {
  display: flex;
  gap: 8px;
}

.chip {
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  height: 44px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--paper);
  color: var(--ink-soft);
  cursor: pointer;
}

.chip:hover { border-color: var(--ink); color: var(--ink); }

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  height: 44px;
  padding: 0 20px;
  margin-left: auto;
  border: 1px solid var(--ink);
  border-radius: 10px;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
}

.btn:hover:not(:disabled) { background: var(--ink); color: var(--paper); }
.btn:disabled { opacity: 0.4; cursor: default; }

/* ---------- Days ---------- */

.days {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.day {
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--paper);
  overflow: hidden;
}

.day-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 22px;
  background: var(--wash);
  border-bottom: 1px solid var(--line);
}

.day-date {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: var(--ink);
}

.day-stats {
  display: flex;
  align-items: baseline;
  gap: 18px;
}

.day-stat {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.day-stat b {
  font-size: 15px;
  color: var(--ink);
  margin-right: 5px;
}

/* Same grey as the labels beside it.
   It used to be amber on a tint, which read as a problem needing attention. An
   unmatched tap usually means somebody left through a door with no reader, or
   is still in the building: worth stating because it explains why the total
   looks low, not a warning. */
.day-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.day-status-icon {
  font-size: 13px;
  line-height: 1;
  letter-spacing: 0;
}

.day-flag {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 3px 0;
}

/* ---------- Events ---------- */

.events {
  list-style: none;
  margin: 0;
  padding: 0;
}

.event {
  display: grid;
  grid-template-columns: 74px 60px 1fr auto;
  align-items: center;
  gap: 14px;
  padding: 13px 22px;
  border-bottom: 1px solid #f1f4f7;
  font-size: 15px;
}

.event:last-child { border-bottom: none; }

.ev-time {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #191c1e;
}

.ev-dir {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 0;
  border-bottom: 2px solid transparent;
}

.ev-in .ev-dir {
  color: var(--in);
  border-bottom-color: var(--in);
}

.ev-out .ev-dir {
  color: var(--ink-soft);
  border-bottom-color: #cbd5e1;
}

.ev-loc {
  color: var(--ink-soft);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Time inside is shown on the exit that closes a visit, so a person who came
   and went twice can see each stretch rather than only a daily total. */
.ev-manual {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 7px;
  border-radius: 5px;
  background: #eef1f4;
  color: var(--ink-soft);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  vertical-align: middle;
}

.ev-dur {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

/* ---------- States ---------- */

.loading {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 32px 0;
}

.pulse {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--accent);
  animation: pulse 1.1s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.2; }
}

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 15px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  margin-left: 10px;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 700px) {
  .log { padding: 0 20px 60px; }
  .btn { margin-left: 0; }
  .event { grid-template-columns: 64px 52px 1fr; }
  .ev-dur { grid-column: 2 / -1; }
}

@media (prefers-reduced-motion: reduce) {
  .pulse { animation: none; }
}
</style>
